import type { PriceUnitDto } from '@/api';
import { FullSizeSpinner } from '@/components/full-size-spinner';
import { DEFAULT_PAGE_MAX_WIDTH } from '@/components/page-wrapper/page-layout';
import { useNotification } from '@/hooks/use-notification';
import { themeColors } from '@/theme/colors';
import { Flex, Text } from '@lego/klik-ui';
import { ThemeProvider, createTheme } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { useKeyFiguresPreviousYear } from '../../simulation-queries';
import { useSimulationQueryErrorNotification } from '../../use-simulation-query-error-notification';
import SimulationTableToolbar from '../components/toolbar/simulation-table-toolbar';
import {
  buildPricelistSettingsMap,
  mapSimulationDataToRowData,
} from '../utils/simulation-helpers';
import type {
  PricelistSettings,
  RowData,
  SimulationTableToolbarProps,
} from '../utils/simulation-types';
import KeyfiguresTable from './keyfigures-table';
import {
  buildKeyfiguresColumns,
  computeKeyfigures,
  filterSimulationRows,
  formatSimulationRows,
} from './keyfigures-view-helpers';

const toolbarTheme = createTheme({ palette: { mode: 'light' } });

type KeyfiguresViewProps = {
  simulationRows: Map<string, RowData>;
  simulationSettings: Map<string, PricelistSettings>;
  asIsSimulationRows: RowData[];
  asIsSettings: Map<string, PricelistSettings>;
  planningYear: number;
  priceUnits: PriceUnitDto[];
  totalLlRrpIndex: Map<string, number>;
  toolbarProps: SimulationTableToolbarProps;
};

const KeyfiguresView = ({
  simulationRows,
  simulationSettings,
  asIsSimulationRows,
  asIsSettings,
  planningYear,
  priceUnits,
  totalLlRrpIndex,
  toolbarProps,
}: KeyfiguresViewProps) => {
  const {
    data: previousYearData,
    isFetching: isFetchingPreviousYear,
    isError: isPreviousYearError,
    error: previousYearError,
  } = useKeyFiguresPreviousYear(toolbarProps.simulationId);

  useSimulationQueryErrorNotification({
    isError: isPreviousYearError,
    error: previousYearError,
    title: 'Failed to load previous year key figures',
  });

  const showNotification = useNotification();
  const unavailablePreviousYear =
    previousYearData?.simulationData === null
      ? previousYearData.previousYear
      : undefined;

  useEffect(() => {
    if (unavailablePreviousYear === undefined) return;
    showNotification({
      type: 'warning',
      title: `No ${unavailablePreviousYear} key figures`,
      description: `No preferred demand forecast exists for ${unavailablePreviousYear}.`,
    });
  }, [unavailablePreviousYear, showNotification]);

  const previousYearKeyfigures = useMemo(() => {
    const settings = buildPricelistSettingsMap(
      previousYearData?.priceSettings,
      previousYearData?.simulationData,
    );
    if (!previousYearData?.simulationData || !settings) return [];
    return computeKeyfigures(
      mapSimulationDataToRowData(previousYearData.simulationData, settings),
      settings,
      priceUnits,
    );
  }, [previousYearData, priceUnits]);

  const asIsKeyfigures = useMemo(
    () => computeKeyfigures(asIsSimulationRows, asIsSettings, priceUnits),
    [asIsSimulationRows, asIsSettings, priceUnits],
  );

  const simulationKeyfigures = useMemo(
    () =>
      computeKeyfigures(
        Array.from(simulationRows.values()),
        simulationSettings,
        priceUnits,
      ),
    [simulationRows, simulationSettings, priceUnits],
  );

  const columnFilters = toolbarProps.columnFilters;
  const formattedSimulationRows = useMemo(
    () =>
      formatSimulationRows({
        rows: Array.from(simulationRows.values()),
        asIsRows: asIsSimulationRows,
        pricelistSettings: simulationSettings,
        totalLlRrpIndex,
      }),
    [simulationRows, asIsSimulationRows, simulationSettings, totalLlRrpIndex],
  );

  const filteredSimulationData = useMemo(
    () =>
      filterSimulationRows({
        rows: Array.from(simulationRows.values()),
        formattedRows: formattedSimulationRows,
        columnFilters: columnFilters ?? [],
      }),
    [simulationRows, formattedSimulationRows, columnFilters],
  );

  const filteredSimulationKeyfigures = useMemo(
    () =>
      computeKeyfigures(filteredSimulationData, simulationSettings, priceUnits),
    [filteredSimulationData, simulationSettings, priceUnits],
  );

  const filteredAsIsKeyfigures = useMemo(() => {
    const filteredRowKeys = new Set(
      filteredSimulationData.map((row) => row.primaryKey),
    );
    return computeKeyfigures(
      asIsSimulationRows.filter((row) => filteredRowKeys.has(row.primaryKey)),
      asIsSettings,
      priceUnits,
    );
  }, [filteredSimulationData, asIsSimulationRows, asIsSettings, priceUnits]);

  return (
    <>
      <ThemeProvider theme={toolbarTheme}>
        <SimulationTableToolbar {...toolbarProps} />
      </ThemeProvider>
      {isFetchingPreviousYear ? (
        <FullSizeSpinner />
      ) : (
        <Flex flex={1} minHeight="0px" overflowY="auto">
          <Flex
            direction="column"
            gap="32px"
            width="100%"
            maxWidth={DEFAULT_PAGE_MAX_WIDTH}
            margin="0 auto"
            padding="32px 16px 16px"
          >
            {simulationKeyfigures.map((row) => {
              const { priceList } = row;
              const columns = buildKeyfiguresColumns({
                planningYear,
                row,
                asIsRows: asIsKeyfigures,
                previousYearRows: previousYearKeyfigures,
                filteredRows: filteredSimulationKeyfigures,
                filteredAsIsRows: filteredAsIsKeyfigures,
              });
              const priceListName = priceList.name ?? priceList.id.toString();
              return (
                <Flex key={priceList.id} direction="column" gap="32px">
                  <Text
                    className="heading-md"
                    color={themeColors.content.default}
                  >
                    {priceListName}
                  </Text>
                  <Flex gap="40px">
                    <Flex flex={1} minWidth="0px" direction="column">
                      <KeyfiguresTable
                        title="Grand total"
                        priceListName={priceListName}
                        firstHeader="GRAND TOTAL"
                        currency={row.currency}
                        columns={columns.grandTotal}
                        highlightColor={
                          themeColors.interactive.primary.subtle.selected
                        }
                      />
                    </Flex>
                    <Flex flex={1} minWidth="0px" direction="column">
                      <KeyfiguresTable
                        title="Filtered"
                        priceListName={priceListName}
                        firstHeader="FILTERED VALUES"
                        currency={row.currency}
                        columns={columns.filteredValues}
                        highlightColor={
                          themeColors.interactive.positive.subtle.selected
                        }
                      />
                    </Flex>
                  </Flex>
                </Flex>
              );
            })}
          </Flex>
        </Flex>
      )}
    </>
  );
};
export default KeyfiguresView;
